import { ComponentType, ForwardedRef, forwardRef, ReactNode } from "react";
import { FieldValues, Path } from "react-hook-form";
import { Writeable } from "utils/typescriptUtilis";
import { TiHiPatch } from "__generated__/gql/graphql";
import Fieldset, { Legend } from "components/Shared/Forms/Fieldset";
import type { ControlledFormFieldProps } from "./FormField";
import NumberInput, { Props as NumberInputProps } from "./NumberInput";
import InlineFieldsWrapper, {
  InlineFieldsWrapperProps,
} from "./InlineFieldsWrapper";

function TiHiInputInner(
  props: NumberInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) {
  return (
    <NumberInput
      ref={ref}
      allowNegative={false}
      max={32767}
      decimalScale={0}
      thousandSeparator
      {...props}
    />
  );
}

const TiHiInput = forwardRef(TiHiInputInner);

export const TI_HI_FIELDS = ["ti", "hi"] as const;

export type TiHiFieldsetType = Writeable<
  Pick<TiHiPatch, (typeof TI_HI_FIELDS)[number]>
>;

type TiHiFields = Path<TiHiFieldsetType>;

export type AllowableFieldValues<
  TFieldValues extends FieldValues,
  TFieldName extends Path<TFieldValues> = Path<TFieldValues>,
> = TFieldName extends `${infer TPrefix}.${TiHiFields}` ? TPrefix : never;

type Props<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  >,
> = InlineFieldsWrapperProps & {
  ControlledFormFieldComponent: ComponentType<
    ControlledFormFieldProps<TFieldValues>
  >;
  fieldNamePrefix: TFieldNamePrefix;
  legend?: ReactNode;
  legendLabel?: ReactNode;
};

export default function TiHiFieldset<
  TFieldValues extends FieldValues,
  TFieldNamePrefix extends Extract<
    Path<TFieldValues>,
    AllowableFieldValues<TFieldValues>
  > = Extract<Path<TFieldValues>, AllowableFieldValues<TFieldValues>>,
>({
  ControlledFormFieldComponent,
  fieldNamePrefix,
  legend,
  legendLabel,
  ...rest
}: Props<TFieldValues, TFieldNamePrefix>) {
  function getPrefixedFieldName(name: TiHiFields) {
    return `${fieldNamePrefix}.${name}` as Path<TFieldValues>;
  }

  return (
    <InlineFieldsWrapper
      as={Fieldset}
      fieldWrapWidth="5em"
      {...rest}
      sx={{
        ...rest.sx,
        "> .chakra-form-control > label": { fontSize: "sm", mb: ".25em" },
      }}
    >
      {legend ||
        (legendLabel && (
          <Legend color="magenta.veryDark" fontSize="md" mb={1}>
            {legendLabel}
          </Legend>
        ))}

      <ControlledFormFieldComponent
        name={getPrefixedFieldName("ti")}
        renderInput={({ field }) => <TiHiInput {...field} />}
      />

      <ControlledFormFieldComponent
        name={getPrefixedFieldName("hi")}
        renderInput={({ field }) => <TiHiInput {...field} />}
      />
    </InlineFieldsWrapper>
  );
}
